type AnalyticsOptions = {
  enabled: boolean;
  measurementId?: string;
};

const state = {
  initialized: false,
  lastPath: '',
};

const toPath = (): string =>
  `${window.location.pathname}${window.location.search}`;

const loadGtag = (measurementId: string) => {
  const scriptId = 'mythea-guide-gtag-script';
  if (document.getElementById(scriptId)) {
    return;
  }
  const script = document.createElement('script');
  script.id = scriptId;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId
  )}`;
  document.head.appendChild(script);
};

export const initAnalytics = ({ enabled, measurementId }: AnalyticsOptions): boolean => {
  const trimmedId = measurementId?.trim();
  if (!enabled || !trimmedId || state.initialized) {
    return state.initialized;
  }

  loadGtag(trimmedId);

  const globalWindow = window as Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  globalWindow.dataLayer = globalWindow.dataLayer || [];
  globalWindow.gtag =
    globalWindow.gtag ||
    function gtag(...args: unknown[]) {
      globalWindow.dataLayer?.push(args);
    };

  globalWindow.gtag('js', new Date());
  globalWindow.gtag('config', trimmedId, { send_page_view: false });
  state.initialized = true;
  return true;
};

export const trackPageView = () => {
  if (!state.initialized) {
    return;
  }

  const globalWindow = window as Window & {
    gtag?: (...args: unknown[]) => void;
  };
  if (typeof globalWindow.gtag !== 'function') {
    return;
  }

  const path = toPath();
  if (path === state.lastPath) {
    return;
  }
  state.lastPath = path;

  globalWindow.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title || 'Mythea Guide',
  });
};

export const registerPageViewTracking = () => {
  if (!state.initialized) {
    return;
  }

  const globalWindow = window as Window & {
    __mytheaGuideLocationTrackingInstalled?: boolean;
  };
  if (globalWindow.__mytheaGuideLocationTrackingInstalled) {
    trackPageView();
    return;
  }
  globalWindow.__mytheaGuideLocationTrackingInstalled = true;

  const notify = () => {
    window.dispatchEvent(new Event('mythea-guide:locationchange'));
  };

  const originalPushState = history.pushState.bind(history);
  history.pushState = function pushState(...args) {
    const value = originalPushState(...args);
    notify();
    return value;
  };

  const originalReplaceState = history.replaceState.bind(history);
  history.replaceState = function replaceState(...args) {
    const value = originalReplaceState(...args);
    notify();
    return value;
  };

  window.addEventListener('popstate', notify);
  window.addEventListener('mythea-guide:locationchange', trackPageView);
  trackPageView();
};
